<template>
  <v-card height="100%" :loading="loading" flat color="bg1">
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title">User Preferences</v-list-item-title>
        <v-list-item-subtitle class="subtitle-1">Customize your application settings</v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
    <v-divider></v-divider>
    <v-form v-model="valid">
      <v-sheet
        color="transparent"
        tile
        class="overflow-hidden pa-4 d-flex flex-column"
        style="gap: 1.5rem">
        
        <!-- Default Landing Page Section -->
        <v-sheet outlined class="pa-4 rounded-lg d-flex flex-wrap">
          <div class="pr-10" style="width: 230px">
            <v-list-item-title class="title">Landing Page</v-list-item-title>
            <v-list-item-subtitle style="white-space: normal">
              Choose which page you want to see when you first log in to the application
            </v-list-item-subtitle>
          </div>
          <v-sheet
            outlined
            style="border-color: #ccc !important"
            color="bg1"
            class="pa-4 rounded-lg flex-1"
            min-width="300px">
            <v-radio-group 
              v-model="preferences.defaultFacilitiesView" 
              :rules="[rules.required]"
              class="mt-0">
              <v-radio
                v-for="option in landingPageOptions"
                :key="option.value"
                :label="option.text"
                :value="option.value"
                color="primary"
                class="mb-2">
                <template v-slot:label>
                  <div class="d-flex flex-column">
                    <span class="font-weight-medium">{{ option.text }}</span>
                    <span class="text-caption text--secondary">{{ option.description }}</span>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-sheet>
        </v-sheet>

        <!-- Save Button -->
        <div class="d-flex justify-end">
          <v-btn
            :disabled="!valid || !hasChanges"
            large
            text
            color="primary"
            :loading="loading"
            @click="savePreferences">
            Save
          </v-btn>
        </div>
      </v-sheet>
    </v-form>
  </v-card>
</template>

<script>
  import { log, warn, error } from '@/utils/debugLogger';
  import { mapGetters } from 'vuex';
  import { LandingPageView } from '@/scripts/js/forgeModels';
  import permissions from '@Share/config/permissions';
  import { $has } from '@/utils';

  export default {
    name: 'PreferencesView',
    data() {
      return {
        valid: false,
        loading: false,
        permissions,
        preferences: {
          defaultFacilitiesView: LandingPageView.Facility,
        },
        originalPreferences: {},
        rules: {
          required: value => !!value || 'This field is required',
        },
      };
    },
    computed: {
      ...mapGetters('ACCOUNT', {
        user: 'GET_USER',
      }),
      userPreferences() {
        return this.user?.userPreferences || {};
      },
      hasChanges() {
        return JSON.stringify(this.preferences) !== JSON.stringify(this.originalPreferences);
      },
      landingPageOptions() {
        const options = [
          {
            value: LandingPageView.Facility,
            text: LandingPageView.getDisplayName(LandingPageView.Facility),
            description: 'View all facilities in a list or grid layout'
          }
        ];

        // Only add Client view option if user has permission
        if (this.$has(this.permissions.Clients.View)) {
          options.push({
            value: LandingPageView.Client,
            text: LandingPageView.getDisplayName(LandingPageView.Client), 
            description: 'View facilities grouped by client'
          });
        }

        return options;
      }
    },
    watch: {
      userPreferences: {
        immediate: true,
        deep: true,
        handler(newPreferences) {
          if (newPreferences) {
            this.loadPreferences();
          }
        }
      },
      landingPageOptions: {
        handler(newOptions) {
          // If current preference is no longer available (user lost permission), reset to default
          const currentPref = this.preferences.defaultFacilitiesView;
          const isCurrentPrefAvailable = newOptions.some(option => option.value === currentPref);
          
          if (!isCurrentPrefAvailable && currentPref === LandingPageView.Client) {
            this.preferences.defaultFacilitiesView = LandingPageView.Facility;
          }
        }
      }
    },
    methods: {
      $has,
      loadPreferences() {
        if (this.user) {
          let defaultView = this.userPreferences.defaultFacilitiesView || LandingPageView.Facility;
          
          // If user preference is Client view but they don't have permission, fallback to Facility view
          if (defaultView === LandingPageView.Client && !this.$has(this.permissions.Clients.View)) {
            defaultView = LandingPageView.Facility;
          }
          
          this.preferences = {
            defaultFacilitiesView: defaultView,
          };
          this.originalPreferences = { ...this.preferences };
        }
      },
      async savePreferences() {
        if (!this.valid) {
          return;
        }

        // Validate that user has permission for Client view if that's what they're trying to save
        if (this.preferences.defaultFacilitiesView === LandingPageView.Client && !this.$has(this.permissions.Clients.View)) {
          this.$dialog.notify.error('You do not have permission to set Client view as default.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.loading = true;
        
        try {
          // Update user preferences in store
          if (!this.user.userPreferences) {
            this.user.userPreferences = {};
          }
          this.user.userPreferences.defaultFacilitiesView = this.preferences.defaultFacilitiesView;
          
          // Save to backend
          await this.$store.dispatch('ACCOUNT/UPDATE_USER_PREFERENCES', this.user.userPreferences);

          this.originalPreferences = { ...this.preferences };
          
          this.$dialog.notify.success('Preferences saved successfully.', {
            position: 'top-right',
            timeout: 3000,
          });
        } catch (err) {
          error('Failed to save preferences:', err);
          this.$dialog.notify.error('Failed to save preferences. Please try again.', {
            position: 'top-right',
            timeout: 3000,
          });
        } finally {
          this.loading = false;
        }
      },
      resetPreferences() {
        this.preferences = {
          defaultFacilitiesView: LandingPageView.Facility,
        };
      }
    },
    mounted() {
      this.loadPreferences();
    }
  };
</script>

<style scoped>
  .v-radio {
    margin-bottom: 8px;
  }
</style> 